import {
  Alert,
  Group,
  GroupItem,
  GroupTitle,
  useFieldLabel,
} from "ra-friendsofbabba";
import React, { useMemo } from "react";
import { Typography, makeStyles } from "@material-ui/core";
import { useQuery, useTranslate } from "react-admin";

import AmountInput from "./AmountInput";
import { get } from "lodash";
import { maxValue } from "ra-core";
import useFormPermission from "../useFormPermission";
import { useFormState } from "react-final-form";

const useStyles = makeStyles((theme) => ({
  total: {
    paddingLeft: theme.spacing(1),
  },
  alert: {
    marginTop: theme.spacing(1),
  },
}));

const AmountGroupInput = ({ ...props }) => {
  const classes = useStyles();
  const hasFormPermission = useFormPermission();
  const fieldLabel = useFieldLabel({ ...props });
  const translate = useTranslate();
  const formState = useFormState();
  const { loading, data } = useQuery({
    type: "get",
    resource: "municipalities/stats",
    payload: {
      id: formState.values.municipality_id,
      project_id: formState.values.id,
    },
  });

  const formatter = Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  });
  const total = useMemo(() => {
    const amounts = [
      "base",
      "engineering",
      "general",
      "unexpected_events",
      "vat_and_other_charges",
      "vat",
    ];
    const sum = amounts
      .map((amount) => get(formState.values, `amount_${amount}`, 0))
      .reduce((a, b) => a + b, 0);

    return +sum.toFixed(2);
  }, [formState.values]);
  const state = get(props, "record.transaction.state");

  const {
    hasRequestedTotalGtTotal,
    hasRequestedTotalGtRemaining,
    requestedTotal,
    requestedAmount,
    remainingAmount,
    muinicipalityName,
  } = useMemo(() => {
    const requestedAmount = get(formState.values, "amount_requested", 0);
    const requestedTotal =
      requestedAmount + get(formState.values, "amount_co_financing", 0);
    const remainingAmount = parseFloat(get(data, "data.remaining_amount", 0));
    const hasRequestedTotalGtTotal = requestedTotal > total;
    const hasRequestedTotalGtRemaining = requestedAmount > remainingAmount;
    const muinicipalityName = get(formState.values, "municipality.name", null);

    return {
      requestedTotal,
      requestedAmount,
      remainingAmount,
      hasRequestedTotalGtTotal,
      hasRequestedTotalGtRemaining,
      muinicipalityName,
    };
  }, [total, formState.values, data]);

  return (
    <Group wrapper>
      <GroupTitle title={fieldLabel(props.source)} />

      {!loading &&
        (!formState.values.transaction ||
          formState.values?.transaction?.state === "draft") && (
          <Alert severity={data?.data?.severity}>
            <div
              dangerouslySetInnerHTML={{
                __html: translate(
                  `resources.projects.municipality_id.${data?.data?.severity}`,
                  {
                    ...data?.data,
                    remaining_amount: formatter.format(
                      data?.data?.remaining_amount
                    ),
                    total_due_amount: formatter.format(
                      data?.data?.total_due_amount
                    ),
                  }
                ),
              }}
            />
          </Alert>
        )}

      <Group>
        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_base"
            disabled={!hasFormPermission("amount_base")}
          />
        </GroupItem>

        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_engineering"
            helperText="resources.projects.fields.amount_engineering.help"
            disabled={!hasFormPermission("amount_engineering")}
          />
        </GroupItem>
      </Group>

      <Group>
        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_general"
            helperText={fieldLabel("amount_general.help")}
            referenceField="amount_base"
            maxPerc={7}
            disabled={!hasFormPermission("amount_general")}
          />
        </GroupItem>

        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_unexpected_events"
            helperText={fieldLabel("amount_unexpected_events.help")}
            referenceField="amount_base"
            maxPerc={10}
            disabled={!hasFormPermission("amount_unexpected_events")}
          />
        </GroupItem>
      </Group>

      <Group>
        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_vat"
            disabled={!hasFormPermission("amount_vat")}
          />
        </GroupItem>

        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_vat_and_other_charges"
            helperText={fieldLabel("amount_vat_and_other_charges.help")}
            disabled={!hasFormPermission("amount_vat_and_other_charges")}
          />
        </GroupItem>
      </Group>

      <Group>
        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_co_financing"
            disabled={!hasFormPermission("amount_co_financing")}
          />
        </GroupItem>

        <GroupItem lg={4} md={6}>
          <AmountInput
            {...props}
            source="amount_requested"
            validate={maxValue(
              "ra.validation.amount_max",
              Math.min(total, remainingAmount)
            )}
            disabled={!hasFormPermission("amount_requested")}
          />
        </GroupItem>
      </Group>

      {state === "draft" && hasRequestedTotalGtRemaining && !loading && (
        <Alert severity="warning">
          <div
            dangerouslySetInnerHTML={{
              __html: translate(
                "resources.projects.messages.requested_total_gt_remaining",
                {
                  ...data?.data,
                  requested_total: formatter.format(requestedAmount),
                  remaining_amount: formatter.format(remainingAmount),
                  municipality_name: muinicipalityName,
                }
              ),
            }}
          />
        </Alert>
      )}
      {state === "draft" && hasRequestedTotalGtTotal && !loading && (
        <Alert severity="warning" className={classes.alert}>
          <div
            dangerouslySetInnerHTML={{
              __html: translate(
                "resources.projects.messages.requested_total_gt_total",
                {
                  ...data?.data,
                  requested_total: formatter.format(requestedTotal),
                  total: formatter.format(total),
                  remaining_amount: formatter.format(remainingAmount),
                }
              ),
            }}
          />
        </Alert>
      )}

      <Group wrapper>
        <GroupTitle title={translate("resources.projects.fields.total")} />
        <Typography variant="h6" className={classes.total}>
          {formatter.format(total)}
        </Typography>
      </Group>
    </Group>
  );
};
export default AmountGroupInput;
